import { canEdit, upsertJournal, canJournal } from '../store.js';
import { getSession, getUserId } from '../supabase-client.js';
import { toDayKey } from '../utils/day-key.js';
import { formatDateIT, formatTimeIT } from '../utils/format.js';
import { escapeHtml } from '../utils/dom.js';
import { resolveTripBases } from '../utils/trip-bases.js';
import { bindTextareaSaveButtons, renderTextareaSaveActions } from '../utils/textarea-save.js';
import { bindTripShell, getTripMainEl, renderTripShell } from './shell.js';

function journalAuthorId(entry) {
  return entry?.user_id ?? entry?.updated_by ?? null;
}

function renderStars(current, { readonly = false } = {}) {
  return [1, 2, 3, 4, 5]
    .map((n) => {
      const active = current >= n;
      if (readonly) {
        return `<span class="star-readonly${active ? ' star-readonly--active' : ''}" aria-hidden="true">★</span>`;
      }
      return `<button type="button" class="star-btn${current === n ? ' star-btn--active' : ''}" data-rating="${n}" aria-label="${n} stelle">★</button>`;
    })
    .join('');
}

function sortMembersForJournal(members, currentUserId) {
  return [...members].sort((a, b) => {
    if (a.user_id === currentUserId) return -1;
    if (b.user_id === currentUserId) return 1;
    return a.display_name.localeCompare(b.display_name, 'it');
  });
}

function renderJournalEntry(member, entry, { isSelf, canWriteJournal, dayKey }) {
  const rating = entry?.rating ?? 0;
  const highlight = entry?.highlight ?? '';
  const updatedAt = entry?.updated_at ? formatTimeIT(entry.updated_at) : '';
  const authorLabel = isSelf ? 'Tu' : escapeHtml(member.display_name);

  if (isSelf) {
    return `
      <article class="journal-entry journal-entry--self" data-user-id="${escapeHtml(member.user_id)}">
        <header class="journal-entry__header">
          <span class="journal-entry__author">
            <span class="journal-entry__emoji" aria-hidden="true">${member.avatar_emoji ?? '👤'}</span>
            <span class="journal-entry__name">${authorLabel}</span>
          </span>
          ${updatedAt ? `<span class="journal-entry__time">${updatedAt}</span>` : ''}
        </header>
        <label class="field">
          <span class="field-label">Momento top della giornata</span>
          <textarea
            class="field-input field-textarea journal-highlight"
            rows="2"
            placeholder="Cosa ricorderai di questa giornata?"
            ${canWriteJournal ? '' : 'disabled'}
          >${escapeHtml(highlight)}</textarea>
        </label>
        <div class="journal-rating">
          <span class="field-label">Voto</span>
          <div class="star-group" data-current="${rating || ''}" role="group" aria-label="Voto giornata">
            ${renderStars(rating)}
          </div>
        </div>
        ${canWriteJournal ? renderTextareaSaveActions(dayKey, 'Salva') : ''}
      </article>
    `;
  }

  if (!entry) return '';

  return `
    <article class="journal-entry journal-entry--readonly" data-user-id="${escapeHtml(member.user_id)}">
      <header class="journal-entry__header">
        <span class="journal-entry__author">
          <span class="journal-entry__emoji" aria-hidden="true">${member.avatar_emoji ?? '👤'}</span>
          <span class="journal-entry__name">${authorLabel}</span>
        </span>
        ${updatedAt ? `<span class="journal-entry__time">${updatedAt}</span>` : ''}
      </header>
      ${
        highlight
          ? `<p class="journal-entry__highlight">${escapeHtml(highlight)}</p>`
          : '<p class="journal-entry__highlight journal-entry__highlight--empty">Nessun commento</p>'
      }
      <div class="journal-rating journal-rating--readonly">
        <span class="field-label">Voto</span>
        <div class="star-group star-group--readonly" aria-label="${rating ? `Voto ${rating} su 5` : 'Nessun voto'}">
          ${rating ? renderStars(rating, { readonly: true }) : '<span class="journal-entry__empty-rating">—</span>'}
        </div>
      </div>
    </article>
  `;
}

function renderJournalContent(ctx, currentUserId) {
  const journalByDayUser = new Map();
  for (const entry of ctx.live.journal) {
    const dayKey = entry.day_key;
    const userId = journalAuthorId(entry);
    if (!userId) continue;
    if (!journalByDayUser.has(dayKey)) journalByDayUser.set(dayKey, new Map());
    journalByDayUser.get(dayKey).set(userId, entry);
  }

  const members = sortMembersForJournal(ctx.live.members, currentUserId);
  const canWriteJournal = canJournal();

  return ctx.days
    .map((day) => {
      const dayKey = toDayKey(day.date);
      const entriesForDay = journalByDayUser.get(dayKey) ?? new Map();
      const entryBlocks = members
        .map((member) =>
          renderJournalEntry(member, entriesForDay.get(member.user_id), {
            isSelf: member.user_id === currentUserId,
            canWriteJournal,
            dayKey,
          }),
        )
        .filter(Boolean)
        .join('');

      return `
        <section class="journal-card" data-day-key="${dayKey}">
          <header class="journal-card__header">
            <h2 class="journal-card__title">${formatDateIT(dayKey)} — ${escapeHtml(day.title)}</h2>
          </header>
          <div class="journal-card__entries">
            ${entryBlocks}
          </div>
        </section>
      `;
    })
    .join('');
}

async function saveJournalEntry(ctx, dayKey, highlight, rating) {
  await upsertJournal(ctx.instance.id, dayKey, highlight, rating || null);
}

async function bindJournal(container, ctx) {
  bindTextareaSaveButtons(container, {
    canSave: () => canJournal(),
    onSave: async (dayKey) => {
      const card = container.querySelector(`.journal-card[data-day-key="${CSS.escape(dayKey)}"]`);
      const entryEl = card?.querySelector('.journal-entry--self');
      if (!entryEl) return;
      const highlightEl = entryEl.querySelector('.journal-highlight');
      const starGroup = entryEl.querySelector('.star-group');
      const rating = Number(starGroup?.dataset.current) || null;
      await saveJournalEntry(ctx, dayKey, highlightEl?.value.trim() ?? '', rating);
    },
  });

  container.querySelectorAll('.journal-entry--self').forEach((entryEl) => {
    const card = entryEl.closest('.journal-card');
    const dayKey = card?.dataset.dayKey;
    if (!dayKey) return;

    const highlightEl = entryEl.querySelector('.journal-highlight');
    const starGroup = entryEl.querySelector('.star-group');

    starGroup?.querySelectorAll('.star-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!canJournal()) return;
        const rating = Number(btn.dataset.rating);
        starGroup.dataset.current = String(rating);
        starGroup.querySelectorAll('.star-btn').forEach((b) => {
          b.classList.toggle('star-btn--active', Number(b.dataset.rating) === rating);
        });
        try {
          await saveJournalEntry(ctx, dayKey, highlightEl.value.trim(), rating);
        } catch (error) {
          alert(error.message);
        }
      });
    });
  });
}

export async function renderJournal(container, ctx) {
  const session = await getSession();
  const currentUserId = getUserId(session);

  container.innerHTML = renderTripShell({
    trip: ctx.trip,
    tripId: ctx.tripId,
    activeTab: 'journal',
    mainHtml: renderJournalContent(ctx, currentUserId),
    members: ctx.live.members,
    bases: resolveTripBases(ctx.live),
  });
  bindTripShell(container, {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  });
  await bindJournal(getTripMainEl(container), ctx);
}

export async function updateJournalMain(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;
  const session = await getSession();
  const currentUserId = getUserId(session);
  main.innerHTML = renderJournalContent(ctx, currentUserId);
  await bindJournal(main, ctx);
}
